import { cn } from '~/utils/cn';

import { DIFF_STATUS_BACKGROUND_CLASS, type DiffStatus } from './diff-status.util';

/** Minimal shape needed here — matches `ProductDetailItem` but avoids a cross-feature type import. */
interface ProductDetailLike {
  sectionName?: string;
  attributeName?: string;
  attributeValue?: string;
  section_name?: string;
  attribute_name?: string;
  attribute_value?: string;
}

interface ProductDetailGroup {
  name: string;
  values: string[];
}

/** Groups items by attribute name, preserving first-seen order and de-duplicating exact-duplicate values within a group. */
function groupByAttributeName(items: ProductDetailLike[]): ProductDetailGroup[] {
  const order: string[] = [];
  const valuesByName = new Map<string, string[]>();

  for (const item of items) {
    const name = item.attributeName ?? item.attribute_name;
    const value = item.attributeValue ?? item.attribute_value;
    if (!name || !value) continue;

    let values = valuesByName.get(name);
    if (!values) {
      values = [];
      valuesByName.set(name, values);
      order.push(name);
    }
    if (!values.includes(value)) values.push(value);
  }

  return order.map((name) => ({ name, values: valuesByName.get(name) ?? [] }));
}

interface ProductDetailCardListProps {
  items: ProductDetailLike[];
  /** Tints each card to match the field's diff status; `'unchanged'` (e.g. the Before row) falls back to a neutral card. */
  status: DiffStatus;
  /**
   * Compact layout for dense grids (the compare grid). Stacks the label above
   * the value, left-aligned — the side-by-side/right-aligned default forces long
   * values like "87% polyester, 13% elastane" to wrap into a ragged column when
   * there are only ~200px to work with.
   */
  dense?: boolean;
  /** Automation hook: list id; each card gets `${testId}-item-{attributeName}`. */
  testId?: string;
}

/** Renders `productDetail` as individual card rows — attribute name on the left, value(s) right-aligned — instead of a bullet list. */
export function ProductDetailCardList({
  items,
  status,
  dense = false,
  testId = 'product-detail-card-list',
}: ProductDetailCardListProps) {
  const bgClass = DIFF_STATUS_BACKGROUND_CLASS[status] || 'bg-muted';
  const groups = groupByAttributeName(items);

  if (dense) {
    return (
      <div className="flex flex-col gap-1" data-testid={testId}>
        {groups.map((group) => (
          <div
            key={group.name}
            className={cn('rounded-sm px-1.5 py-1', bgClass)}
            data-testid={`${testId}-item-${group.name}`}
          >
            <span className="text-muted-foreground block text-[10px] leading-3 font-medium uppercase">
              {group.name}
            </span>
            {group.values.map((value, i) => (
              <span key={i} className="text-foreground block text-xs leading-4 font-medium">
                {value}
              </span>
            ))}
          </div>
        ))}
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-2" data-testid={testId}>
      {groups.map((group) => (
        <div
          key={group.name}
          data-testid={`${testId}-item-${group.name}`}
          className={cn(
            'flex items-center justify-between gap-4 rounded-sm px-4 py-3 text-sm',
            bgClass,
          )}
        >
          <span className="text-foreground font-medium">{group.name}</span>
          <div className="flex flex-col items-end gap-0.5 text-right">
            {group.values.map((value, i) => (
              <span key={i} className="text-foreground font-semibold">
                {value}
              </span>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}
